import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { siteSettingsApi } from "../../../service/certificate"
import CertCompetitions from "../competitions"
import CertSports from "../sports"
import CertSignatures from "../signatures"
import CertAttributions from "../attributions"
import CertFonts from "../fonts"

// Certificate Settings — RMS-parity tabbed hub. Tab 1 is our School Details
// (placeholder source); the rest host the existing master-data managers as
// tabs, exactly like RMS's Settings page (Competitions / Sports / Signatures /
// Attributions / Fonts). Each manager component is rendered untouched; the
// `.cert-embed` wrapper zeroes the nested .page-wrapper offset so it lays out
// inside the tab instead of re-applying the sidebar margin.
type TabKey = "school" | "competitions" | "sports" | "signatures" | "attributions" | "fonts"

const TABS: { key: TabKey; label: string }[] = [
  { key: "school", label: "School Details" },
  { key: "competitions", label: "Competitions" },
  { key: "sports", label: "Sports" },
  { key: "signatures", label: "Signatures" },
  { key: "attributions", label: "Attributions" },
  { key: "fonts", label: "Fonts" },
]

const CertSettings = () => {
  const [tab, setTab] = useState<TabKey>("school")

  // ── School Details form state ──────────────────────────────────────
  const [form, setForm] = useState<Record<string, string>>({
    schoolName: "",
    schoolCode: "",
    address: "",
    phone: "",
  })
  const [logo, setLogo] = useState<File | null>(null)
  const [logoUrl, setLogoUrl] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const res = await siteSettingsApi.get()
      if (res.success) {
        const d = res.data || {}
        setForm({
          schoolName: d.schoolName || "",
          schoolCode: d.schoolCode || "",
          address: d.address || "",
          phone: d.phone || "",
        })
        setLogoUrl(d.logoUrl || "")
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load settings")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => fd.append(k, v))
      if (logo) fd.append("logo", logo)
      const res = await siteSettingsApi.update(fd)
      if (res.success) {
        toast.success("Settings saved")
        setLogo(null)
        if (res.data?.logoUrl) setLogoUrl(res.data.logoUrl)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page-wrapper">
      {/* Neutralise the nested manager components' own .page-wrapper so they
          render inside the tab, not offset by the sidebar margin again. */}
      <style>{`
        .cert-embed .page-wrapper { margin: 0; padding: 0; }
        .cert-embed .page-wrapper .content { padding: 0; }
      `}</style>
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Certificate Settings</h3>
          <p className="text-muted mb-0">
            Manage the school details and the reusable lists used when generating certificates.
          </p>
        </div>

        <ul className="nav nav-tabs nav-tabs-solid mb-3" role="tablist">
          {TABS.map(t => (
            <li className="nav-item" key={t.key} role="presentation">
              <button
                type="button"
                className={`nav-link${tab === t.key ? " active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            </li>
          ))}
        </ul>

        {tab === "school" && (
          <div className="card">
            <div className="card-body">
              <p className="text-muted">
                School details that fill the <code>{"{{school.name}}"}</code>, <code>{"{{school.code}}"}</code>,{" "}
                <code>{"{{campus.address}}"}</code>, <code>{"{{campus.phone}}"}</code> and{" "}
                <code>{"{{campus.logo}}"}</code> placeholders on certificates.
              </p>
              {loading ? (
                <div className="text-center p-4"><span className="spinner-border text-primary" /></div>
              ) : (
                <form onSubmit={save} style={{ maxWidth: 640 }}>
                  <div className="mb-3">
                    <label className="form-label">School Name</label>
                    <input
                      className="form-control"
                      value={form.schoolName}
                      onChange={e => setForm({ ...form, schoolName: e.target.value })}
                      placeholder="e.g. The EON Academy"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">School Code</label>
                    <input
                      className="form-control"
                      value={form.schoolCode}
                      onChange={e => setForm({ ...form, schoolCode: e.target.value })}
                      placeholder="e.g. EON"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.address}
                      onChange={e => setForm({ ...form, address: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone</label>
                    <input
                      className="form-control"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">School Logo</label>
                    {logoUrl && (
                      <div className="mb-2">
                        <img src={logoUrl} alt="logo" style={{ height: 60, objectFit: "contain", border: "1px solid #eee", borderRadius: 4, padding: 4 }} />
                      </div>
                    )}
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={e => setLogo(e.target.files?.[0] || null)}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? "Saving..." : "Save Settings"}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Master-data managers, rendered untouched inside the tab. Only the
            active tab mounts, so each fetches its own data on demand. */}
        {tab === "competitions" && <div className="cert-embed"><CertCompetitions /></div>}
        {tab === "sports" && <div className="cert-embed"><CertSports /></div>}
        {tab === "signatures" && <div className="cert-embed"><CertSignatures /></div>}
        {tab === "attributions" && <div className="cert-embed"><CertAttributions /></div>}
        {tab === "fonts" && <div className="cert-embed"><CertFonts /></div>}
      </div>
    </div>
  )
}

export default CertSettings
